"use client"

import { useState, useCallback, useMemo } from "react"
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { useAuth } from '../context/AuthContext'
import { convertToWei } from "../utils/walletUtils"
import { useDispatch, useSelector } from 'react-redux'
import { getPrivateGasEstimate, privateTransfer, clearPrivateTransferError } from '../redux/slices/walletSlice'

// Components
import TransferSummary from "./TransferSummary"

// Hooks
import useTransferForm from "../hooks/useTransferForm"


// Constants
import { STEPS, TRANSFER_CONFIG, BUTTON_STYLES, MODAL_TITLES } from "../constants/transferConstants"

// Small inline components
const TokenDisplay = ({ selectedToken }) => (
  <Form.Group className="mb-3">
    <Form.Label>Token to Transfer</Form.Label>
    <div className="form-control">
      {selectedToken ? (
        <div className="d-flex align-items-center gap-3">
          <img
            src={selectedToken.image}
            alt={selectedToken.name}
            width="32"
            height="32"
            className="rounded-circle"
          />
          <span>{selectedToken.name} ({selectedToken.balance} {selectedToken.symbol})</span>
        </div>
      ) : (
        <span className="text-muted">No token selected</span>
      )}
    </div>
  </Form.Group>
)

const NetworkFeeDisplay = ({ fee = "Unable to fetch fee" }) => (
  <div className="card">
    <div className="card-body">
      <h6 className="fw-semibold mb-3">Transfer Fee</h6>
      <div className="d-flex justify-content-between">
        <span>Network Fee:</span>
        <span>{fee}</span>
      </div>
    </div>
  </div>
)

const TransactionDetails = ({ amount, selectedToken, txHash, fee, status = "Processing..." }) => (
  <div className="p-3 bg-light rounded">
    <h6>Transaction Details</h6>
    <div className="d-flex justify-content-between mb-1">
      <span>Amount:</span>
      <span>{amount} {selectedToken?.symbol}</span>
    </div>
    <div className="d-flex justify-content-between mb-1">
      <span>Network Fee:</span>
      <span>{fee}</span>
    </div>
    <div className="d-flex justify-content-between mb-1">
      <span>Status:</span>
      <span className="text-warning">{status}</span>
    </div>
    <div className="d-flex justify-content-between">
      <span>Transaction ID:</span>
      <small className="font-monospace">{txHash || 'Pending...'}</small>
    </div>
  </div>
)

const AmountInput = ({
  amount,
  useMaxAmount,
  selectedToken,
  validationErrors,
  onAmountChange,
  onMaxClick
}) => (
  <Form.Group className="mb-3">
    <Form.Label className="fw-semibold text-dark">Amount</Form.Label>
    <div className="input-group">
      <Form.Control
        type="number"
        placeholder="0.00"
        value={amount}
        onChange={onAmountChange}
        onWheel={(e) => e.target.blur()}
        className="form-input form-control m-0"
        isInvalid={!!validationErrors.amount}
      />
      <Button
        variant={useMaxAmount ? "primary" : "outline-primary"}
        onClick={onMaxClick}
        className="fw-semibold px-3 w-auto"
        style={{
          borderColor: "var(--accent-color)",
          color: useMaxAmount ? "#fff" : "var(--accent-color)",
          backgroundColor: useMaxAmount ? "var(--accent-color)" : "transparent",
        }}
      >
        MAX
      </Button>
    </div>
    {validationErrors.amount && (
      <Form.Control.Feedback type="invalid" className="d-block">
        {validationErrors.amount}
      </Form.Control.Feedback>
    )}
    {useMaxAmount && (
      <Form.Text className="small mt-1 d-block">
        Using all available tokens ({selectedToken?.balance} {selectedToken?.symbol})
      </Form.Text>
    )}
  </Form.Group>
)

const AddressInput = ({ label, value, onChange, disabled = false, placeholder, helpText, isInvalid, errorMessage }) => (
  <Form.Group className="mb-3">
    <Form.Label className="fw-semibold text-dark">{label}</Form.Label>
    <Form.Control
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`form-input form-control ${disabled ? 'bg-light' : ''}`}
      style={{ fontFamily: 'monospace' }}
      isInvalid={isInvalid}
    />
    {isInvalid ? (
      <Form.Control.Feedback type="invalid" className="d-block">
        {errorMessage}
      </Form.Control.Feedback>
    ) : helpText ? (
      <Form.Text className="text-muted small mt-1">
        {helpText}
      </Form.Text>
    ) : null}
  </Form.Group>
)

const SuccessIcon = ({ size = 64 }) => (
  <div className="mb-3 d-flex justify-content-center">
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="#28a745" />
      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
)

// Main component
function PrivateTransferModal({ onClose, selectedToken }) {

  const [showErrors, setShowErrors] = useState(false)
  const [recipientError, setRecipientError] = useState("");


  const { user } = useAuth()
  const { wallet } = user
  const [step, setStep] = useState(STEPS.FORM)
  const dispatch = useDispatch()
  const privateGasEstimate = useSelector(state => state.wallet.privateGasEstimate)
  const privateGasEstimateLoading = useSelector(state => state.wallet.privateGasEstimateLoading)

  const {
    amount,
    recipientAddress,
    useMaxAmount,
    isValidForm,
    validationErrors,
    handleAmountChange,
    handleMaxClick,
    handleRecipientChange
  } = useTransferForm(selectedToken)

  const {
    privateTransferLoading: loading,
    privateTransferError: apiError,
    privateTransferTxHash: txHash
  } = useSelector(state => state.wallet)

  const executeTransfer = async (transferData) => {
    const result = await dispatch(privateTransfer(transferData))
    if (privateTransfer.fulfilled.match(result)) {
      setStep(STEPS.SUCCESS)
      return { success: true }
    }
    return { success: false }
  }

  const clearError = () => dispatch(clearPrivateTransferError())

  // Step navigation
  const handleNext = useCallback(() => {
    if (!isValidForm) {
      setShowErrors(true)
      return
    }

    if (step < STEPS.SUCCESS) {
      const next = step + 1
      // If moving from FORM -> CONFIRMATION, request private gas estimate
      if (step === STEPS.FORM && next === STEPS.CONFIRMATION) {
        dispatch(getPrivateGasEstimate({ tokenId: selectedToken?.id, amount, recipientAddress }))
      }
      setStep(next)
      clearError()
      setShowErrors(false)
    }
  }, [step, clearError, dispatch, selectedToken, amount, recipientAddress, isValidForm])

  const handleBack = useCallback(() => {
    if (step > STEPS.FORM) {
      setStep(step - 1)
      clearError()
    }
  }, [step, clearError])

  // Transfer execution
  const handleConfirm = useCallback(async () => {
    const transferData = {
      tokenId: selectedToken?.id,
      amount: amount,
      recipientAddress,
    }

    const result = await executeTransfer(transferData)
    if (result.success) {
      setStep(STEPS.SUCCESS)
    }
  }, [selectedToken, amount, recipientAddress, wallet?.railgunAddress, executeTransfer])


  // Button configurations
  const buttonConfig = useMemo(() => ({
    [STEPS.FORM]: {
      cancel: { text: "Cancel", style: BUTTON_STYLES.OUTLINE, onClick: onClose },
      primary: {
        text: "Transfer Tokens",
        style: BUTTON_STYLES.PRIMARY,
        onClick: handleNext,
        // disabled: !isValidForm // Removing disabled to allow clicking and showing errors
      }
    },
    [STEPS.CONFIRMATION]: {
      back: { text: "Back", style: BUTTON_STYLES.OUTLINE, onClick: handleBack },
      primary: {
        text: loading ? "Processing..." : "Confirm Transfer",
        style: BUTTON_STYLES.PRIMARY,
        onClick: handleConfirm,
        // disable confirm while executing transfer or while gas estimate is loading or missing
        disabled: loading || privateGasEstimateLoading || !privateGasEstimate,
        loading
      }
    },
    [STEPS.SUCCESS]: {
      primary: { text: "Done", style: BUTTON_STYLES.PRIMARY, onClick: onClose }
    }
  }), [isValidForm, loading, onClose, handleNext, handleBack, handleConfirm, privateGasEstimateLoading, privateGasEstimate])

  return (
    <Modal
      show={true}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="custom--modal private-transfer--modal"
    >
      <div className="modal-header m-0 border-bottom">
        <h2>{MODAL_TITLES[step]}</h2>
        <button className="close-button" onClick={onClose}>
          ✕
        </button>
      </div>

      <Modal.Body>
        {step === STEPS.FORM && (
          <>
            <Alert variant="info" className="mb-3" style={{ fontSize: '13px' }}>
              {`You can send tokens between private Kaxaa piggybanks.`}
            </Alert>

            <Form>
              <TokenDisplay selectedToken={selectedToken} />

              <AmountInput
                amount={amount}
                useMaxAmount={useMaxAmount}
                selectedToken={selectedToken}
                validationErrors={showErrors ? validationErrors : {}}
                onAmountChange={(e) => {
                  handleAmountChange(e);
                  if (showErrors) setShowErrors(false);
                }}
                onMaxClick={handleMaxClick}
              />

              <AddressInput
                label="Recipient Address"
                value={recipientAddress}
                onChange={(e) => {
                  handleRecipientChange(e);
                  if (showErrors) setShowErrors(false);
                }}
                placeholder="Enter private address (0zk...)"
                isInvalid={showErrors && !!validationErrors.recipient}
                errorMessage={validationErrors.recipient}
                helpText="Enter the recipient's private address"
              />

              <AddressInput
                label="Sender Address"
                value={wallet?.railgunAddress || ""}
                disabled={true}
                helpText="Your private address"
              />

              <NetworkFeeDisplay fee={
                privateGasEstimate ? (
                  // try common fields then fallback to raw
                  `${privateGasEstimate} WEI`
                ) : (
                  privateGasEstimateLoading ? 'Calculating...' : TRANSFER_CONFIG.NETWORK_FEE
                )
              } />
            </Form>
          </>
        )}

        {step === STEPS.CONFIRMATION && (
          <>
            <Alert variant="warning" className="mb-3" style={{ fontSize: '13px' }}>
              <h6 className="fw-semibold mb-1">Confirm Transaction</h6>
              Please review the details below before proceeding with the private transfer.
            </Alert>

            {apiError && <div className="error-alert mb-3">{apiError}</div>}

            <TransferSummary
              selectedToken={selectedToken}
              amount={amount}
              senderAddress={wallet?.railgunAddress}
              recipientAddress={recipientAddress}
            />
            <div className="mt-3">
              <NetworkFeeDisplay fee={
                privateGasEstimate ? (
                  `${privateGasEstimate} WEI`
                ) : (
                  privateGasEstimateLoading ? 'Calculating...' : TRANSFER_CONFIG.NETWORK_FEE
                )
              } />
            </div>
          </>
        )}

        {step === STEPS.SUCCESS && (
          <>
            <Alert variant="success" className="mb-3">
              <strong>Transaction Submitted Successfully!</strong>
            </Alert>

            <div className="text-center mb-4">
              <SuccessIcon />
              <h5>Your tokens are being transferred!</h5>
            </div>

            <Alert variant="info" className="mb-3">
              <strong>Processing Time:</strong><br />
              The private transfer can take <strong>{TRANSFER_CONFIG.PROCESSING_TIME}</strong> to complete.
            </Alert>

            <Alert variant="primary" className="mb-3">
              <strong>Email Confirmation:</strong><br />
              A confirmation email has been sent to your registered email address with transaction details.
            </Alert>

            <TransactionDetails
              amount={amount}
              selectedToken={selectedToken}
              txHash={txHash}
              fee={privateGasEstimate ? (privateGasEstimate.fee || privateGasEstimate.total || JSON.stringify(privateGasEstimate)) : TRANSFER_CONFIG.NETWORK_FEE}
            />
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {Object.entries(buttonConfig[step]).map(([key, config]) => (
          <Button
            key={key}
            variant={key === 'primary' ? 'primary' : 'outline-secondary'}
            onClick={config.onClick}
            disabled={config.disabled}
            style={config.style}
          >
            {config.loading && (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            )}
            {config.text}
          </Button>
        ))}
      </Modal.Footer>
    </Modal>
  )
}

export default PrivateTransferModal